import {CARDS} from "./constants";
import {shuffleArray} from "../../utils/utils";
import {
    updateCards,
    updateCurrent,
    updateFlipComplete,
    updateFlipped,
    updateProcessing,
    updateSelected,
    updateCollections,
    restoreFlipState,
} from "./actions";

export const setCollections = (collections) => {
    return (dispatch) => {
        return new Promise((resolve) => {
            return resolve(dispatch(updateCollections(collections)));
        });
    }
}

export const setCards = (collection) => {
    return (dispatch) => {
        return new Promise((resolve) => {
            const base = CARDS[collection];
            if (!base || !base.cards) return resolve();
            const shuffledCards = shuffleArray([...base.cards]);
            const collectionToDispatch = { ...base, cards: shuffledCards };
            return resolve(dispatch(updateCards(collectionToDispatch)));
        });
    }
}

/**
 * Restore collection and progress from persisted state (shuffled order, no deal animation).
 */
export const restoreFlipStateFromPersistence = (collectionName, flippedCardNames) => {
    return (dispatch) => {
        const base = CARDS[collectionName];
        if (!base || !base.cards) {
            return Promise.resolve();
        }
        const shuffledCards = shuffleArray([...base.cards]);
        const collection = { ...base, cards: shuffledCards };
        const flipped = shuffledCards.filter((c) => flippedCardNames.includes(c.name));
        dispatch(restoreFlipState({ collection, flipped }));
        return Promise.resolve();
    };
};

export const setCurrentCard = (card, pause = 800) => {
    return (dispatch) => {
        return new Promise((resolve) => {
            dispatch(updateCurrent(card));
            dispatch(updateSelected([card], !card.name));
            // apply the pause, time to show the card before we trigger any other actions
            return setTimeout(() => resolve(card), pause);
        });
    }
}

export const checkCards = (card = false) => {
    return (dispatch, getState) => {
        return new Promise((resolve) => {
            const {flip} = getState();
            if (card.name) {
                flip.selected.push(card);
            }
            const current = card || flip.current;
            const selectedGroupSibling = Object.values(flip.selected).filter(value => {
                return value.name !== current.name;
            })[0];
            const currentGroupSiblings = Object.values(flip.collection.cards).filter(value => {
                return value.group === current.group;
            });
            const selectedGroup = selectedGroupSibling ? selectedGroupSibling.group : false;

            let updateSelectedArr = [];
            let removeSelected = false;
            let updateFlippedArr = [];
            let removeFlipped = false;
            let status = "current_success";

            // Check for the supplied card already matching the current
            if (card && getState().flip.current && getState().flip.current.name === card.name) {
                status = "current_failed";
            }
            // Check for a failed group match
            else if (selectedGroup && current.group !== selectedGroup) {
                updateSelectedArr = flip.selected;
                removeSelected = true;
                status = "group_failed";
            }
            // Check for a successful full group match
            else if (flip.selected.length === currentGroupSiblings.length) {
                updateFlippedArr = currentGroupSiblings;
                removeFlipped = false;
                updateSelectedArr = flip.selected;
                removeSelected = true;
                status = "group_full";
            }
            if (card.name) {
                flip.selected.pop();
            }

            return resolve({
                updateFlippedArr,
                removeFlipped,
                updateSelectedArr,
                removeSelected,
                status
            });
        });
    }
}

export const flipCards = (pause = 800) => {
    return (dispatch) => {
        return new Promise((resolve) => {
            dispatch(checkCards()).then((response) => {
                if (response.updateSelectedArr.length) {
                    dispatch(updateSelected(response.updateSelectedArr, response.removeSelected));
                }
                if (response.updateFlippedArr.length) {
                    dispatch(updateFlipped(response.updateFlippedArr, response.removeFlipped));
                }

                // apply the pause, time to show the card before we trigger any other actions
                return setTimeout(() => resolve(), pause);
            });
        });
    }
}

export const chooseCard = (card) => {
    return (dispatch, getState) => {

        if (getState().flip.processing || card.flipped) {
            return Promise.resolve();
        }

        dispatch(updateProcessing(true));

        // Clicked the current card, revert the selection

        if (getState().flip.current && getState().flip.current.name === card.name) {
            return dispatch(setCurrentCard({}, 80))
                .then(() => {
                    dispatch(updateProcessing(false));
                    dispatch(flipCards()).then(() => {
                        dispatch(updateProcessing(false));
                    });
                });
        }

        // Set the currently selected card, and run the flip card action to progress the game

        return dispatch(checkCards(card)).then((response) => {
            console.log(response);
            // Set the delay based on the reason in the resonse
            let delay = 80;
            if (response.status === "group_failed") {
                delay = 800;
            }
            else if (response.status === "group_full") {
                delay = 800;
            }
            else if (response.status === "current_success") {
                delay = 10;
            }
            dispatch(setCurrentCard(card, delay))
                .then(() => {
                    return dispatch(flipCards(80)).then(() => {
                        dispatch(updateProcessing(false));
                    });
                })
                .then(() => {
                    // END GAME
                    if (getState().flip.flipped.length + getState().flip.selected.length >= getState().flip.collection.cards.length) {
                        setTimeout(() => {
                            dispatch(updateProcessing(false));
                            dispatch(updateFlipComplete(true));
                        },1000);
                    }
                });
        });
    }
}

export const resetGame = (pause = 800) => {
    return (dispatch, getState) => {
        if (getState().flip.processing) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            const wasComplete = getState().flip.complete;
            dispatch(updateProcessing(true));
            dispatch(updateFlipped(getState().flip.flipped, true));
            dispatch(updateSelected(getState().flip.selected, true));
            dispatch(updateFlipComplete(false));

            if (wasComplete) {
                dispatch(updateProcessing(false));
                dispatch(setCards(getState().flip.collection.name));
                return resolve();
            }
            setTimeout(() => {
                dispatch(updateProcessing(false));
                dispatch(setCards(getState().flip.collection.name));
                return resolve();
            }, pause);
        });
    }
}

/**
 * Switch to a new collection (e.g. from select change). Resets game state and
 * sets the new collection immediately with no delay, so the old deck is not shown.
 */
export const switchCollection = (collectionName) => {
    return (dispatch, getState) => {
        if (getState().flip.processing) {
            return Promise.resolve();
        }
        dispatch(updateFlipped(getState().flip.flipped, true));
        dispatch(updateSelected(getState().flip.selected, true));
        dispatch(updateFlipComplete(false));
        return dispatch(setCards(collectionName));
    };
}

